import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProject, appendSectionNote } from '../api';
import { useToast } from '../App';
import { useAuth } from '../context/AuthContext';
import UploadModal from '../components/UploadModal';
import ScopeEditor from '../components/ScopeEditor';
import ScopeLedger from '../components/ScopeLedger';
import {
  ChevronDown, ChevronUp, FileText,
  CheckCircle, Ban, CalendarDays, PlusCircle, FileUp,
  RefreshCw, Plus, Sparkles, Loader, AlertTriangle, ShieldCheck
} from '../components/Icons';
import './ProjectDetail.css';

const SECTION_CONFIG = [
  {
    key: 'deliverables',
    label: 'Deliverables',
    icon: CheckCircle,
    colorClass: 'section-success',
    accentVar: '#22c55e',
    description: 'Work items included in this project',
    placeholder: 'e.g. Include mobile-responsive designs for all pages',
  },
  {
    key: 'outOfScope',
    label: 'Out of Scope',
    icon: Ban,
    colorClass: 'section-danger',
    accentVar: '#ef4444',
    description: 'Explicitly excluded from this contract',
    placeholder: 'e.g. Does not include backend API development',
  },
  {
    key: 'timelines',
    label: 'Timelines',
    icon: CalendarDays,
    colorClass: 'section-accent',
    accentVar: '#3b82f6',
    description: 'Milestones and delivery dates',
    placeholder: 'e.g. Final delivery extended to March 30th per email',
  },
  {
    key: 'extras',
    label: 'Agreed Additions',
    icon: PlusCircle,
    colorClass: 'section-primary',
    accentVar: '#8b5cf6',
    description: 'Amendments agreed after signing',
    placeholder: 'e.g. Client agreed to 2 extra revision rounds for $200',
  },
];

function SectionNoteInput({ sectionKey, projectId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef(null);
  const { addToast } = useToast();

  const config = SECTION_CONFIG.find(s => s.key === sectionKey);

  function handleOpen() {
    setOpen(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }

  async function handleSave() {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const updated = await appendSectionNote(projectId, sectionKey, text.trim());
      onSaved(updated);
      setText('');
      setOpen(false);
      addToast('Note added to scope', 'success');
    } catch (err) {
      addToast('Failed to save note', 'error');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSave();
    if (e.key === 'Escape') { setOpen(false); setText(''); }
  }

  if (!open) {
    return (
      <button className="section-add-note-btn" onClick={handleOpen}>
        <Plus size={13} />
        Add a note
      </button>
    );
  }

  return (
    <div className="section-note-input-area">
      <textarea
        ref={textareaRef}
        className="section-note-textarea"
        rows={2}
        placeholder={config?.placeholder || 'Type your note...'}
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={loading}
      />
      <div className="section-note-actions">
        <span className="section-note-hint">⌘ Enter to save</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className="btn btn-secondary"
            style={{ padding: '6px 12px', fontSize: '0.8rem' }}
            onClick={() => { setOpen(false); setText(''); }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            style={{ padding: '6px 12px', fontSize: '0.8rem' }}
            onClick={handleSave}
            disabled={!text.trim() || loading}
          >
            {loading ? <div className="spinner" /> : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScopeSection({ sectionKey, label, icon: Icon, colorClass, accentVar, description, items, sectionNotes, projectId, onProjectUpdated }) {
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll]   = useState(false);

  const VISIBLE_LIMIT = 4; // items shown before "Show more"
  const totalCount  = items.length + (sectionNotes?.length || 0);
  const visibleItems = showAll ? items : items.slice(0, VISIBLE_LIMIT);
  const hiddenCount  = items.length - VISIBLE_LIMIT;

  return (
    <div className={`scope-section-block ${colorClass}`} style={{ '--section-accent': accentVar }}>
      {/* Header */}
      <button className="scope-block-header" onClick={() => setExpanded(e => !e)}>
        <div className="scope-block-header-left">
          <div className="scope-block-icon-wrap">
            <Icon size={15} />
          </div>
          <div>
            <div className="scope-block-label">{label}</div>
            <div className="scope-block-desc">{description}</div>
          </div>
        </div>
        <div className="scope-block-header-right">
          <span className="scope-block-count">{totalCount}</span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {expanded && (
        <div className="scope-block-body">
          {/* Empty state */}
          {totalCount === 0 && (
            <div className="scope-empty-state">
              Nothing extracted from document — add your own notes below.
            </div>
          )}

          {/* AI-Extracted Items */}
          {items.length > 0 && (
            <div className="scope-items-group">
              <div className="scope-items-group-label">
                <Sparkles size={11} />
                From document
              </div>
              <div className="scope-chips-grid">
                {visibleItems.map((item, i) => (
                  <div key={i} className="scope-chip">
                    <span className="scope-chip-dot" />
                    <span>{typeof item === 'object' ? item.text : item}</span>
                  </div>
                ))}
              </div>
              {/* Show more / less toggle */}
              {!showAll && hiddenCount > 0 && (
                <button className="scope-show-more-btn" onClick={() => setShowAll(true)}>
                  Show {hiddenCount} more item{hiddenCount !== 1 ? 's' : ''} ↓
                </button>
              )}
              {showAll && items.length > VISIBLE_LIMIT && (
                <button className="scope-show-more-btn" onClick={() => setShowAll(false)}>
                  Show less ↑
                </button>
              )}
            </div>
          )}

          {/* User-added Section Notes */}
          {sectionNotes?.length > 0 && (
            <div className="scope-items-group scope-items-user">
              <div className="scope-items-group-label">
                <Plus size={11} />
                Your additions
              </div>
              <div className="scope-chips-grid">
                {sectionNotes.map((note, i) => (
                  <div key={i} className="scope-chip scope-chip-user">
                    <span className="scope-chip-dot" />
                    <span>{typeof note === 'object' ? note.text : note}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Note */}
          <div className="scope-block-footer">
            <SectionNoteInput
              sectionKey={sectionKey}
              projectId={projectId}
              onSaved={onProjectUpdated}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Contract Strength Card ─────────────────────────────────────────────────────
const STRENGTH_COLOR = { strong: '#22c55e', moderate: '#f59e0b', weak: '#ef4444' };
const SEV_CONFIG = {
  high:   { color: '#ef4444', bg: 'rgba(239,68,68,0.08)',   border: 'rgba(239,68,68,0.2)'   },
  medium: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.2)'  },
  low:    { color: '#22c55e', bg: 'rgba(34,197,94,0.08)',   border: 'rgba(34,197,94,0.2)'   },
};

function ContractStrengthCard({ analysis }) {
  if (!analysis) return null;

  const { overallStrength, scopeCreepVulnerability, risks, protections, recommendations } = analysis;
  const badgeColor = STRENGTH_COLOR[overallStrength] || '#f59e0b';

  return (
    <div className="contract-strength-card card">
      {/* Header */}
      <div className="cs-header">
        <div className="cs-header-left">
          <div className="cs-icon" style={{ color: badgeColor, background: `${badgeColor}18`, borderColor: `${badgeColor}30` }}>
            <ShieldCheck size={18} />
          </div>
          <div>
            <div className="cs-title-row">
              <div className="cs-title">Contract Strength Analysis</div>
              {overallStrength && (
                <span className={`cs-strength-badge cs-badge-${overallStrength}`}>{overallStrength}</span>
              )}
            </div>
            <div className="cs-subtitle">AI-powered SOW health report against 10 industry standards</div>
          </div>
        </div>
      </div>

      {/* Vulnerability summary */}
      {scopeCreepVulnerability && (
        <p className="cs-vulnerability">{scopeCreepVulnerability}</p>
      )}

      {/* Risks + Protections */}
      <div className="cs-grid">
        {risks?.length > 0 && (
          <div className="cs-panel">
            <div className="cs-panel-label cs-label-risk">Risks identified &mdash; {risks.length} found</div>
            <div className="cs-panel-items">
              {risks.map((r, i) => {
                const cfg = SEV_CONFIG[r.severity] || SEV_CONFIG.medium;
                return (
                  <div key={i} className="cs-risk-item"
                    style={{ '--sev-color': cfg.color, '--sev-bg': cfg.bg, '--sev-border': cfg.border }}>
                    <div className="cs-risk-row">
                      <span className="cs-sev-badge">{r.severity}</span>
                      <span className="cs-risk-title">{r.title}</span>
                    </div>
                    {r.description && <p className="cs-risk-desc">{r.description}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {protections?.length > 0 && (
          <div className="cs-panel">
            <div className="cs-panel-label cs-label-protect">Protections in place &mdash; {protections.length} found</div>
            <div className="cs-panel-items">
              {protections.map((p, i) => (
                <div key={i} className="cs-protect-item">
                  <div className="cs-protect-title">{p.title}</div>
                  {p.description && <p className="cs-protect-desc">{p.description}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recommendations */}
      {recommendations?.length > 0 && (
        <div className="cs-recommendations">
          <div className="cs-panel-label cs-label-rec">What to add to strengthen this contract</div>
          <ul className="cs-rec-list">
            {recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { planMeta } = useAuth();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const pollRef = useRef(null);

  const isLocked = !planMeta?.ledger;
  
  const fetchProject = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getProject(id);
      setProject(data);
    } catch (err) {
      addToast('Project not found', 'error');
      navigate('/app');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Auto-poll when scope is processing — stops when done/failed
  useEffect(() => {
    function startPolling() {
      pollRef.current = setInterval(async () => {
        try {
          const data = await getProject(id);
          setProject(data);
          if (data.scopeStatus !== 'processing') {
            clearInterval(pollRef.current);
            if (data.scopeStatus === 'done') {
              addToast('Scope extracted from your document!', 'success');
            }
          }
        } catch { /* ignore poll errors silently */ }
      }, 8000);
    }

    if (project?.scopeStatus === 'processing') {
      startPolling();
    }
    return () => clearInterval(pollRef.current);
  }, [project?.scopeStatus, id, addToast]);

  if (loading) return null;
  if (!project) return null;

  const scope = project.scopeKnowledge || {};
  const hasScope = scope.rawSummary || scope.deliverables?.length > 0;

  return (
    <div className="project-detail animate-fade-in">
      <div className="breadcrumb">
        <button className="breadcrumb-back" onClick={() => navigate('/app')}>← Dashboard</button>
        <span className="breadcrumb-sep">/</span>
        <span>{project.projectName}</span>
      </div>

      <div className="page-header">
        <div className="project-detail-title">
          <div className="project-detail-avatar">
            {(project.projectName || '?').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <h1>{project.projectName}</h1>
            {project.clientName && <p className="page-title-sub">Client: {project.clientName}</p>}
          </div>
        </div>
        <div className="project-detail-actions">
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>
            {hasScope ? <><RefreshCw size={16} /> Re-upload PDF</> : <><FileUp size={16} /> Upload Scope PDF</>}
          </button>
        </div>
      </div>

      {/* Processing banner */}
      {project.scopeStatus === 'processing' && (
        <div className="scope-processing-banner">
          <Loader size={16} className="spin" />
          <div>
            <strong>Analyzing your document…</strong>
            <span>AI is extracting your scope — this page will update automatically in a few seconds.</span>
          </div>
        </div>
      )}

      {/* Failed banner */}
      {project.scopeStatus === 'failed' && (
        <div className="scope-failed-banner">
          <AlertTriangle size={18} style={{ flexShrink: 0 }} />
          <div>
            <strong>Processing failed</strong>
            <span>{project.scopeError || 'Something went wrong during extraction.'}</span>
          </div>
          <button className="btn btn-secondary" onClick={() => setShowUpload(true)}>Try again</button>
        </div>
      )}

      {scope.rawSummary && (
        <div className="scope-summary-card card">
          <div className="scope-summary-icon">
            <Sparkles size={22} color="var(--primary)" />
          </div>
          <div>
            <div className="scope-summary-label">AI Scope Summary</div>
            <p className="scope-summary-text">{scope.rawSummary}</p>
            {scope.processedAt && (
              <div className="scope-summary-meta">
                Processed from <strong>{scope.sourceFile}</strong> · {new Date(scope.processedAt).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      )}

      {scope.contractAnalysis && (
        <ContractStrengthCard analysis={scope.contractAnalysis} />
      )}

      {!hasScope && (
        <div className="no-scope-banner card">
          <div className="no-scope-banner-icon"><FileText size={42} color="var(--primary)" /></div>
          <div>
            <strong>No scope document uploaded yet</strong>
            <p>Upload your client contract PDF to automatically extract deliverables, exclusions, and timelines.</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowUpload(true)}>Upload PDF</button>
        </div>
      )}

      {(hasScope || true) && (
        <div className="scope-sections-grid">
          {SECTION_CONFIG.map(({ key, label, icon, colorClass, accentVar, description, placeholder }) => {
            const items = (scope[key] || []).filter(item => !(typeof item === 'object' && item._isUserNote));
            const sectionNotes = scope.sectionNotes?.[key] || [];
            const totalCount = items.length + sectionNotes.length;

            // Always show section if has scope (for the add-note ability), but skip completely empty ones if no PDF yet
            if (!hasScope) return null;
            if (totalCount === 0 && items.length === 0 && sectionNotes.length === 0) {
              // Show collapsed placeholder so user can still add notes
              return (
                <ScopeSection
                  key={key}
                  sectionKey={key}
                  label={label}
                  icon={icon}
                  colorClass={colorClass}
                  accentVar={accentVar}
                  description={description}
                  items={[]}
                  sectionNotes={[]}
                  projectId={id}
                  onProjectUpdated={setProject}
                />
              );
            }

            return (
              <ScopeSection
                key={key}
                sectionKey={key}
                label={label}
                icon={icon}
                colorClass={colorClass}
                accentVar={accentVar}
                description={description}
                items={items}
                sectionNotes={sectionNotes}
                projectId={id}
                onProjectUpdated={setProject}
              />
            );
          })}
        </div>
      )}

      <ScopeLedger project={project} hasScope={hasScope} />

      <ScopeEditor projectId={id} onScopeUpdated={setProject} locked={isLocked} />

      {showUpload && (
        <UploadModal
          projectId={id}
          projectName={project.projectName}
          onSuccess={() => {
            setShowUpload(false);
            addToast('Document received — extracting scope in the background…', 'info');
            // Trigger an immediate refresh so the processing banner appears right away
            getProject(id).then(setProject).catch(() => {});
          }}
          onClose={() => setShowUpload(false)}
        />
      )}
    </div>
  );
}
